import { ChartData, ChartOptions, Plugin } from 'chart.js';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { merge } from 'lodash';
import { useTheme } from 'src/providers/ThemeProvider';
import { CHART_GRID, CHART_TICK, chartMode } from 'src/styles/chartTheme';

const defaultBarChartOptions: Partial<ChartOptions<'bar'>> = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  elements: {
    point: {
      radius: 0,
    },
  },
  scales: {
    x: {
      grid: {
        drawOnChartArea: false,
        color: 'rgba(0, 0, 0, 0)',
      },
      border: {
        display: false,
      },
      ticks: {
        padding: 9,
        color: CHART_TICK,
      },
      stacked: true,
    },
    y: {
      min: 0,
      max: 200,
      ticks: {
        stepSize: 50,
        padding: 9,
        color: CHART_TICK,
      },
      border: {
        display: false,
      },
      stacked: true,
    },
  },
};

const BarChart: React.FC<{
  data: ChartData<'bar', number[], string>;
  options?: Partial<ChartOptions<'bar'>>;
  onBarClick?: (label: string, timestamp: string, index: number) => void;
  plugins?: Plugin<'bar'>[];
}> = ({ data, options = defaultBarChartOptions, onBarClick, plugins }) => {
  const { theme } = useTheme();
  const gridColor = CHART_GRID[chartMode(theme)];
  const mergedOptions = merge({}, defaultBarChartOptions, { scales: { y: { grid: { color: gridColor } } } }, options);
  mergedOptions.onClick = (evt, elements) => {
    if (elements.length === 0) return;

    const element = elements[0];
    const { datasetIndex, index } = element;

    const datasetLabel = data.datasets[datasetIndex].label as string;
    const datasetTimeStamp = data.labels?.[index] as string;

    if (onBarClick) onBarClick(datasetLabel, datasetTimeStamp, index);
  };

  return (
    <div style={{ position: 'relative', width: '100%', contain: 'inline-size' }}>
      <Bar
        data={data}
        options={mergedOptions}
        plugins={plugins}
        height="auto"
        width="auto"
        style={{ maxHeight: 300 }}
      />
    </div>
  );
};

export default BarChart;
